import React, { useEffect, useState, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { 
  AreaChart, Area, 
  BarChart, Bar, 
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f43f5e'];

const Reports = () => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState({
    countries: [],
    revenue: [],
    staff: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.role !== 'Admin') {
      setError('You do not have permission to view this page.');
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        const [countryRes, revRes, staffRes] = await Promise.all([
          api.get('/reports/applications-by-country'),
          api.get('/reports/revenue-by-month'),
          api.get('/reports/staff-performance')
        ]);
        
        setData({
          countries: countryRes.data,
          revenue: revRes.data,
          staff: staffRes.data
        });
      } catch (err) {
        setError('Failed to load reports. Make sure you have Admin rights.');
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [user]);

  if (loading) return <div>Loading reports...</div>;
  if (error) return <div className="text-red-500 font-bold p-8 card">{error}</div>;

  // Format Staff Data for the Bar Chart
  const formattedStaffData = data.staff.map(s => ({
    ...s,
    completionRate: s.total_assigned > 0 ? Math.round((s.completed_tasks / s.total_assigned) * 100) : 0
  }));

  // Custom tooltip for Revenue
  const CustomRevenueTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-100 shadow-xl rounded-lg">
          <p className="font-bold text-gray-800">{label}</p>
          <p className="text-emerald-600 font-bold">NRs {parseFloat(payload[0].value).toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Analytics & Reports</h1>

      {/* Revenue Trend - Full Width */}
      <div className="card mb-8">
        <h2 className="text-lg font-bold mb-6 text-gray-800">Revenue Trends</h2>
        <div className="h-80 w-full">
          {data.revenue.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-400">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue} margin={{ top: 10, right: 30, left: 20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 12}} />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: '#6b7280', fontSize: 12}}
                  tickFormatter={(value) => `Rs ${value >= 1000 ? (value/1000) + 'k' : value}`}
                />
                <Tooltip content={<CustomRevenueTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Applications by Country - Pie Chart */}
        <div className="card">
          <h2 className="text-lg font-bold mb-6 text-gray-800">Applications by Country</h2>
          <div className="h-80 w-full">
            {data.countries.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.countries}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="country"
                    label={({ country, percent }) => `${country} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {data.countries.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Staff Performance - Bar Chart */}
        <div className="card">
          <h2 className="text-lg font-bold mb-6 text-gray-800">Staff Task Performance</h2>
          <div className="h-80 w-full">
            {data.staff.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400">No data available</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formattedStaffData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="username" axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 12}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#6b7280', fontSize: 12}} allowDecimals={false} />
                  <Tooltip 
                    cursor={{fill: '#f9fafb'}}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend verticalAlign="top" height={36}/>
                  <Bar dataKey="total_assigned" name="Assigned Tasks" fill="#e5e7eb" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed_tasks" name="Completed Tasks" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
